"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.validateAddress = void 0;
const parseAddress_1 = require("../utilities/parseAddress");
const ValidationResponse_1 = require("./ValidationResponse");
const CITY_MAX_LENGTH = 30;
const CITY_MIN_LENGTH = 2;
const ADDRESS_MAX_LENGTH = 40;
const ADDRESS_MIN_LENGTH = 2;
/**
 * Validate a street address.
 * @param {object} params - Parameters.
 * @param {string} params.a - The address to be validated.
 * @param {boolean} params.optional - Whether the address is optional.
 * @returns {ValidationResponse} - Validation response.
 */
const validateStreet = ({ a, optional, allowPOBox, }) => {
    if (optional && a.length === 0) {
        return ValidationResponse_1.ValidationResponse.VALID;
    }
    // po box regex
    const re = /(p|p\s|p\.|post)(\s|)(((o\s|o|o\.)|(0\s|0|0\.))|office)(\s|)(box)\b(.*?)/gim;
    if (a.toLowerCase().match(re) && !allowPOBox) {
        return ValidationResponse_1.ValidationResponse.ADDRESS_INVALID_POBOX;
    }
    if (a.length > ADDRESS_MAX_LENGTH) {
        return ValidationResponse_1.ValidationResponse.ADDRESS_INVALID_LONG;
    }
    if (a.length < ADDRESS_MIN_LENGTH) {
        return ValidationResponse_1.ValidationResponse.ADDRESS_INVALID_SHORT;
    }
    return ValidationResponse_1.ValidationResponse.VALID;
};
/**
 * Validate a ZIP code.
 * @param {string} z - The ZIP code to be validated.
 * @returns {ValidationResponse} - Validation response.
 */
const validateZip = (z) => {
    if (z.length < 5 || isNaN(Number(z))) {
        return ValidationResponse_1.ValidationResponse.ZIP_INVALID;
    }
    return ValidationResponse_1.ValidationResponse.VALID;
};
/**
 * Validate a city name.
 * @param {string} c - The city to be validated.
 * @returns {ValidationResponse} - Validation response.
 */
const validateCity = (c) => {
    if (c.length > CITY_MAX_LENGTH) {
        return ValidationResponse_1.ValidationResponse.CITY_INVALID_LONG;
    }
    if (c.length < CITY_MIN_LENGTH) {
        return ValidationResponse_1.ValidationResponse.CITY_INVALID_SHORT;
    }
    return ValidationResponse_1.ValidationResponse.VALID;
};
/**
 * Validate a state name. TODO: Implement logic to validate states.
 * @param {string} s - The state to be validated.
 * @param {boolean} compareRestricted - Whether to compare against restricted states. (auth users doesn't need this)
 * @returns {ValidationResponse} - Validation response.
 */
const restrictedStates = ["NV", "NH", "OR", "VT"];
const validateState = (s, compareRestricted) => {
    if (s.length !== 2) {
        return ValidationResponse_1.ValidationResponse.STATE_INVALID;
    }
    if (compareRestricted && restrictedStates.includes(s)) {
        return ValidationResponse_1.ValidationResponse.STATE_INVALID_RESTRICTED;
    }
    return ValidationResponse_1.ValidationResponse.VALID;
};
/**
 * Validate an address text.
 * @param {string} data - The address data to be validated.
 * @returns {string} - the validation error if invalid, null if valid.
 */
const validateAddressCRM = (data) => {
    const address = (0, parseAddress_1.parseAddress)(data);
    const { streetLine1, streetLine2, city, state, country, zipCode } = address;
    if (validateStreet({ a: streetLine1, allowPOBox: true }) !== ValidationResponse_1.ValidationResponse.VALID) {
        return "StreetLine1 invalid";
    }
    if (validateCity(city) !== ValidationResponse_1.ValidationResponse.VALID) {
        return "City invalid";
    }
    if (validateState(state) !== ValidationResponse_1.ValidationResponse.VALID) {
        return "State invalid";
    }
    const countryPattern = /^[A-Z]{2}$/;
    if (!countryPattern.test(country)) {
        return "Country must consist of 2 uppercase letters";
    }
    if (validateZip(zipCode) !== ValidationResponse_1.ValidationResponse.VALID) {
        return "ZipCode invalid";
    }
    return null; // No validation errors
};
exports.validateAddress = {
    street: validateStreet,
    zip: validateZip,
    city: validateCity,
    state: validateState,
    address: validateAddressCRM,
};
